// src/pages/RoomListPage.jsx

import React, { useEffect, useState } from "react";
import { getAllRooms } from "../services/roomService";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Typography,
  Container,
  Grid,
  Button,
  CircularProgress,
  Box,
  Paper
} from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import RoomCard from "../components/RoomCard";

function RoomListPage() {
  const [rooms, setRooms] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  
  // Get user from Redux store
  const user = useSelector((state) => state.user.user);
  
  // Check if user is organization admin (same logic as Navbar)
  const isOrgAdmin = () => {
    if (!user || !user.organization || !user.organization.users) return false;
    const entry = user.organization.users.find(
      (e) => e.user && e.user._id && e.user._id === user._id
    );
    return entry ? entry.role === "admin" : false;
  };

  useEffect(() => {
    async function fetchData() {
      try {
        setIsLoading(true);
        setError("");
        
        // Fetch rooms
        const roomsData = await getAllRooms();
        console.log('Fetched rooms:', roomsData); // Debug log to see if images are included
        
        setRooms(roomsData);
      } catch (err) {
        console.error("Failed to load data:", err);
        setError("Failed to load rooms");
      } finally {
        setIsLoading(false);
      }
    }
    fetchData();
  }, [user]);

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          mt: 4,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container sx={{ mt: 4 }}>
        <Typography variant="h4" gutterBottom>
          Room List
        </Typography>
        <Typography color="error">{error}</Typography>
      </Container>
    );
  }

  return (
    <Container sx={{ mt: 4 }}>
      <Typography variant="h4" gutterBottom>
        Room List
      </Typography>

      <Grid container spacing={2}>
        {rooms.map((room) => (
          <Grid item xs={12} sm={6} md={4} key={room._id}>
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              {/* Use RoomCard component for image display */}
              <RoomCard room={room} />
              
              {/* Add booking button */}
              <Box sx={{ p: 2, pt: 0 }}>
                <Button
                  size="small"
                  variant="contained"
                  component={Link}
                  to={`/book-room/${room._id}`}
                  fullWidth
                >
                  Book This Room
                </Button>
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>
      
      {rooms.length === 0 && (
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Paper sx={{ p: 4, maxWidth: 400, mx: 'auto' }}>
            <Box sx={{ mb: 3 }}>
              <AddIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom>
                No Meeting Rooms Yet
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                {isOrgAdmin() 
                  ? "Create your first meeting room to get started!"
                  : "Your organization doesn't have any meeting rooms set up yet."
                }
              </Typography>
            </Box>
            
            {isOrgAdmin() ? (
              <Button
                variant="contained"
                size="large"
                component={Link}
                to="/create-room"
                startIcon={<AddIcon />}
                sx={{ mt: 2 }}
              >
                Create First Room
              </Button>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Contact your administrator to add rooms.
              </Typography>
            )}
          </Paper>
        </Box>
      )}
    </Container>
  );
}

export default RoomListPage;